import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button } from '@mui/material';
import verifiedIcon from '@src/assets/icons/pro-voice-cloning-option.svg';
import LockIcon from '@mui/icons-material/Lock';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import {
  PACKAGE_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useLocation from '@src/hooks/useLocation';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import useUserInfo from '@src/hooks/useTtsUser';
import { useDispatch, useSelector } from 'react-redux';
import useWindowDimensions from '@src/hooks/useWindowDimensions';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import actions from '@src/redux/actions';
import { BREAKPOINTS } from '@src/styles/config';
import ROUTES from '@src/constants/route';
import { CRM_URL, LANDING_PAGE_URL } from '@src/configs';
import { TIME_EXPIRE_QR_VOICE_CLONING } from '@src/constants/voiceCloning';
import QRDialog from '@src/containers/VoiceCloning/QRDialog';
import CrownImg from '@src/assets/images/crown.png';
import ExclamationMark from '@src/assets/images/warning.png';
import apis from '@src/apis';
import ProvideEmailToUseFeatureDialog from '@src/containers/Layout/RequireProvideEmailDialog/ProvideEmailToUseFeatureDialog';
import { checkUserHasProvidedEmail } from '@src/services/email';
import NewFeature from '@src/new-components/Chip/NewFeature';
import { StyledVoiceCloningOption } from './index.style';
import StartVoiceCloningAction from './StartVoiceCloningAction';

const ProVoiceCloningOption = ({
  numOfProVoiceCloning = 0,
  handleOpenPopupProfessionalVoiceCloneUpload,
}) => {
  const { keycloak } = useCustomKeycloak();
  const history = useHistory();
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  const { getFeatureValue } = useFeatureFlags();
  const { ip } = useLocation();
  const { t } = useTranslation();

  const { user } = useSelector((state) => state.auth);
  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();

  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrExpireDate, setQrExpireDate] = useState('');
  const [
    showPopupLimitNumberVoiceCloning,
    setShowPopupLimitNumberVoiceCloning,
  ] = useState(false);
  const [showButtonUpgrade, setShowButtonUpgrade] = useState(false);
  const [packageVipYear, setPackageVipYear] = useState(null);
  const [openProvideEmailDialog, setOpenProvideEmailDialog] = useState(false);
  const isValidNumberOfVoicesCanUse = useMemo(() => {
    if (!ttsUser || numOfProVoiceCloning == null) return true;

    const maxVoices = ttsUser.maxCloningVoices ?? 0;
    return numOfProVoiceCloning < maxVoices;
  }, [numOfProVoiceCloning, ttsUser]);

  const hasVoiceCloningFeature = getFeatureValue(FEATURE_KEYS.VOICE_CLONING, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
    screenWidth: window.innerWidth,
  });

  const apiTtsSttLaunchEnabled = getFeatureValue(
    FEATURE_KEYS.LAUNCH_API_TTS_STT,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
      ip,
    },
  );

  const hasProvidedEmail = checkUserHasProvidedEmail(user);

  const redirectUrl = `${CRM_URL}${ROUTES.VOICE_CLONING_CREATE}`;

  const canUseVoiceCloning = useMemo(() => {
    const userPackageCanUseVoiceCloning = validateUserPackageCanUseVoiceCloning(
      { ttsUser, isUserInWhiteList: hasVoiceCloningFeature },
    );

    return userPackageCanUseVoiceCloning;
  }, [ttsUser?.packageExpiryDate, hasVoiceCloningFeature]);

  const generateQRCodeUrl = (loginCode) => {
    const screenApp = 'voiceCloning';
    return `${LANDING_PAGE_URL}/apps/aivoice-tts?screen=${screenApp}&loginCode=${loginCode}`;
  };

  const handleOpenQR = async () => {
    const loginCode = await keycloak.generateLoginCode(redirectUrl);
    if (!loginCode) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorGenerateQRCode'),
        }),
      );
      return;
    }

    const url = generateQRCodeUrl(loginCode);
    if (!url) return;

    setQrExpireDate(moment().add(TIME_EXPIRE_QR_VOICE_CLONING, 'milliseconds'));
    setQrCodeUrl(url);
    setOpenQRDialog(true);
  };

  const handleCloseQRDialog = () => setOpenQRDialog(false);

  const handleShowPopupLimitNumberVoiceCloning = (isVipPackage) => {
    setShowPopupLimitNumberVoiceCloning(true);
    setShowButtonUpgrade(!isVipPackage);
  };

  const handleClosePopupLimitNumberVoiceCloning = () => {
    setShowPopupLimitNumberVoiceCloning(false);
  };

  const handleValidateNumberClonedVoice = () => {
    const isVipPackage = usingPackage?.code === packageVipYear;
    handleShowPopupLimitNumberVoiceCloning(isVipPackage);
    return null;
  };

  const handleUpgradePackage = () => {
    history.push(ROUTES.PAYMENT);
    dispatch(actions.voiceVoiceCloning.closePopupVoiceCloningOptions());
  };

  const handleFetchPackages = async () => {
    const res = await apis.packages.getPackages({
      active: true,
      purchasable: true,
      type: PACKAGE_TYPE.STUDIO,
    });
    if (res?.status) {
      const foundPackageVipYear = res?.result?.packages?.find(
        ({ code }) =>
          code.includes(PACKAGE_LEVEL.VIP) &&
          code.includes(PACKAGE_DURATION.YEAR),
      );
      setPackageVipYear(foundPackageVipYear);
    }
  };

  const renderActionComponent = () => {
    if (!showButtonUpgrade) {
      return (
        <Button
          variant="contained"
          onClick={handleClosePopupLimitNumberVoiceCloning}
        >
          {t('gotItV2')}
        </Button>
      );
    }

    return (
      <>
        <Button
          className="outlined-button"
          onClick={handleClosePopupLimitNumberVoiceCloning}
        >
          {t('gotItV2')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleClosePopupLimitNumberVoiceCloning();
            handleUpgradePackage();
          }}
        >
          {t('upgradeNow')}
        </Button>
      </>
    );
  };

  useEffect(() => {
    handleFetchPackages();
  }, []);

  return (
    <StyledVoiceCloningOption isProVoiceCloning>
      <div className="header">
        <div className="icon-container">
          <img src={verifiedIcon} alt="verified" className="icon" />
        </div>
        <div className="badge-wrapper">
          <Typography variant="body-14-medium" color={COLOR.indigo.default}>
            {t('proVoiceCloningProperty')}
          </Typography>
        </div>
      </div>
      <div className="description-wrapper">
        <div className="label-wrapper">
          <Typography variant="body-24-bold" color={COLOR.black.default}>
            {t('proVoiceCloningOption')}
          </Typography>
          {!apiTtsSttLaunchEnabled && <NewFeature />}
        </div>
        <Typography variant="body-14-regular" color={COLOR.label2[64]}>
          {t('proVoiceCloningDescription')}
        </Typography>
      </div>
      {canUseVoiceCloning ? (
        <div className="action-wrapper">
          <StartVoiceCloningAction
            isValidNumberOfVoicesCanUse={isValidNumberOfVoicesCanUse}
            handleValidateNumberClonedVoice={handleValidateNumberClonedVoice}
            handleOpenPopupProfessionalVoiceCloneUpload={
              handleOpenPopupProfessionalVoiceCloneUpload
            }
            handleOpenQR={handleOpenQR}
            hasProvidedEmail={hasProvidedEmail}
            onOpenProvideEmailDialog={() => setOpenProvideEmailDialog(true)}
            apiTtsSttLaunchEnabled={apiTtsSttLaunchEnabled}
          />
        </div>
      ) : (
        <div className="upgrade-package-action-wrapper">
          <div className="upgrade-package-desc">
            <div className="icon-wrapper">
              <LockIcon className="icon" />
            </div>
            <Typography variant="body-14-regular" color={COLOR.secondary[100]}>
              {t('requirePackageNote')}
            </Typography>
          </div>
          <div className="upgrade-package-action">
            <CommonButton
              fullWidth={width < BREAKPOINTS.MLG}
              variant="outlineSmall"
              onClick={handleUpgradePackage}
              textProps={{
                variantText: 'body-14-medium',
                color: COLOR.secondary[100],
              }}
              customStyle={{
                padding: '9px 12px',
              }}
            >
              {t('upgradePackageAction')}
            </CommonButton>
          </div>
        </div>
      )}
      {openQRDialog && (
        <QRDialog
          open={openQRDialog}
          urlToShow={qrCodeUrl}
          qrExpireDate={qrExpireDate}
          qrRedirectUrl={redirectUrl}
          onClose={handleCloseQRDialog}
          refreshQRCode={handleOpenQR}
        />
      )}

      {showPopupLimitNumberVoiceCloning && (
        <NotificationDialogV4
          image={showButtonUpgrade ? CrownImg : ExclamationMark}
          title={t('voiceCloningLimitTitle')}
          description={
            showButtonUpgrade
              ? t('voiceCloningLimitDescWithUpgrade', {
                  numberVoices: ttsUser?.maxCloningVoices || 0,
                })
              : t('voiceCloningLimitDescWithNoUpgrade', {
                  numberVoices: ttsUser?.maxCloningVoices || 0,
                })
          }
          actionComponent={renderActionComponent()}
          open={showPopupLimitNumberVoiceCloning}
          onClose={handleClosePopupLimitNumberVoiceCloning}
        />
      )}
      {openProvideEmailDialog && (
        <ProvideEmailToUseFeatureDialog
          onClose={() => setOpenProvideEmailDialog(false)}
        />
      )}
    </StyledVoiceCloningOption>
  );
};

export default ProVoiceCloningOption;
